/**
 * dtrModel.js — the DTR report's pure model builder (PRD_DtrReport, Step 3).
 *
 * buildDtrModel is PURE: no jsPDF, no Firestore, no Date.now() — `today` is an
 * input. Every automated criterion in the PRD asserts on this object; the
 * drawing layer (dtrPdfService.js) only renders it. Same split as
 * payslipBuilders.js (pure, tested) + payslipPdfService.js (draw-only).
 *
 * The per-cell formats REPRODUCE the screen's formatRecordDisplay
 * (EmployeeTimeRecords.js:619-637) as an accepted fork (C5) — that function is
 * component-local and unexported. The formats are pinned by exact matchers in
 * dtrModel.test.js so paper and screen cannot drift silently.
 */
import { eachDayOfInterval, endOfMonth } from 'date-fns';
import { toYMDString, parsePeriodToDate, formatYMDForDisplay, manilaYMD } from '../utils/periodDate';
import { formatFirestoreTimestamp, sortRecordsByTimeIn } from '../utils/timeFormatting';
import { dayFlagOf, DAY_FLAGS } from '../utils/dayFlags';
import { computeMonthStats } from '../utils/dtrMonthStats';

/** The C18 outside-employment-window marker — one string, reused by the test. */
export const NOT_EMPLOYED_MARKER = 'Not employed';

/**
 * PRD_DtrDateRange C9's wholly-future notice — exported so the Attendance
 * Summary model can recognise the branch (it replaces the table body on it,
 * PRD_AttendanceSummaryReport C12) without a second copy of the string.
 */
export const FUTURE_RANGE_NOTICE = 'No records — this range is in the future';

/**
 * C18's roster predicate: is the employee employed on ANY day of the range?
 * One window-overlap comparison — NOT two boundary-day tests, which silently
 * drop an employee hired and terminated inside the same range. Dates go
 * through toYMDString (absorbs Timestamp | Date | 'YYYY-MM-DD'), the
 * periodDate.js rule.
 *
 * @param {Object} employee - employee doc (reads employment.startDate/endDate)
 * @param {string} rangeStartYMD - 'YYYY-MM-DD' first day of the range
 * @param {string} rangeEndYMD - 'YYYY-MM-DD' last day of the range
 * @returns {boolean}
 */
export const isEmployedInRange = (employee, rangeStartYMD, rangeEndYMD) => {
  const startYMD = toYMDString(employee?.employment?.startDate);
  const endYMD = toYMDString(employee?.employment?.endDate);
  // No start date recorded: treat as employed (matches isEmployeeActiveOnDate's
  // "missing boundary is open" reading in employeeFilters.js).
  return (
    (!startYMD || startYMD <= rangeEndYMD) && (!endYMD || endYMD >= rangeStartYMD)
  );
};

/**
 * ICU 72+ separates the time from AM/PM with U+202F NARROW NO-BREAK SPACE.
 * Fine on screen; unproven in the embedded PDF font's glyph table — normalize
 * to a plain space so the PDF can never print a notdef box (impl-review #2).
 */
const fmtTime = (t) => {
  const s = formatFirestoreTimestamp(t);
  return s ? s.replace(/\u202F/g, ' ') : s;
};

/**
 * Screen-parity cell formats for one record (C5's accepted fork of
 * formatRecordDisplay). '—' placeholders, toFixed(2) hours, "N min" break,
 * the full DAY_FLAGS label, and the overnight `+1` derivation (C10).
 */
const formatRecordCells = (record) => {
  const isOvernight = Boolean(
    record.timeOut &&
      record.timeOut.toDate &&
      manilaYMD(record.timeOut.toDate()) !== record.date // Manila day (C3)
  );
  const flagKey = dayFlagOf(record);

  return {
    timeIn: fmtTime(record.timeIn) || '—',
    timeOut: fmtTime(record.timeOut) || '—',
    overnight: isOvernight,
    flag: flagKey ? DAY_FLAGS[flagKey].label : null,
    otHours: record.overtimeHours?.toFixed(2) || '—',
    nightDiff: record.nightDiffHours?.toFixed(2) || '—',
    utHours: (record.undertimeHours || 0).toFixed(2),
    regularHours: record.regularHours?.toFixed(2) || '—',
    breakStr: record.breakMinutes ? `${record.breakMinutes} min` : '—',
  };
};

const EMPTY_CELLS = {
  timeIn: '',
  timeOut: '',
  overnight: false,
  flag: null,
  otHours: '',
  nightDiff: '',
  utHours: '',
  regularHours: '',
  breakStr: '',
};

/**
 * Build the DTR report model for one employee and one validated date range.
 * The DTR report caps its range at 31 days (PRD_DtrDateRange C4); the
 * Attendance Summary calls this at up to ~62 (PRD_AttendanceSummaryReport C3)
 * — the caller's validation is what bounds it, nothing in here does.
 *
 * Callers pass a validated range (PRD_DtrDateRange C5) — an inverted range
 * yields a reversed day list, not an error (date-fns v4 eachDayOfInterval
 * reverses; it does not throw).
 *
 * @param {Object} args
 * @param {Object} args.company - company doc; header prints `name` ONLY (C6)
 * @param {Object} args.employee - employee doc (personalInfo, employment)
 * @param {Array}  args.records - merged timeRecords from every month document
 *   the range spans (PRD_DtrDateRange C7)
 * @param {string} args.rangeStartYMD - 'YYYY-MM-DD' first day of the range
 * @param {string} args.rangeEndYMD - 'YYYY-MM-DD' last day of the range
 * @param {Date}   args.today - "now", injected so the builder stays pure (C11)
 * @returns {{header: Object, rows: Array, totals: Object, partialStamp: string|null}}
 */
export const buildDtrModel = ({ company, employee, records, rangeStartYMD, rangeEndYMD, today }) => {
  const days = eachDayOfInterval({
    start: parsePeriodToDate(rangeStartYMD),
    end: parsePeriodToDate(rangeEndYMD),
  });
  // "Today" is the MANILA date of the passed instant, not the browser's local
  // date — the AU-based operator sits 2-3h ahead of Manila, and a local-clock
  // day here would print a month as final while its last Manila day is still
  // live (same class as employeeFilters.js's todayManilaYMD; impl-review #1).
  // en-CA yields ISO 'YYYY-MM-DD'. Pure: derived from the `today` input.
  const todayYMD = today.toLocaleDateString('en-CA', { timeZone: 'Asia/Manila' });

  const empStartYMD = toYMDString(employee?.employment?.startDate);
  const empEndYMD = toYMDString(employee?.employment?.endDate);

  // Map records onto their clock-in date (each record stays on record.date —
  // the month document is complete by construction, C10).
  const recordsByDate = {};
  (records || []).forEach((record) => {
    if (!recordsByDate[record.date]) recordsByDate[record.date] = [];
    recordsByDate[record.date].push(record);
  });

  const rows = [];
  const dayGroups = [];
  days.forEach((dateObj) => {
    const dateYMD = toYMDString(dateObj);
    const dayAbbr = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
    const isWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6;
    // Chronological within the day, same helper the screen uses (C5 parity).
    const dayRecords = sortRecordsByTimeIn(recordsByDate[dateYMD]);
    dayGroups.push({ records: dayRecords });

    if (dayRecords.length === 0) {
      // A recorded day outside the employment window prints its records (data
      // wins); only a RECORDLESS outside-window day carries the marker — it is
      // a KNOWN non-day and must not look like an unknown blank one (C18/C12).
      const outsideWindow =
        (empStartYMD && dateYMD < empStartYMD) || (empEndYMD && dateYMD > empEndYMD);
      rows.push({
        date: dateYMD,
        dayAbbr,
        isWeekend,
        kind: outsideWindow ? 'not-employed' : 'empty',
        ...EMPTY_CELLS,
        ...(outsideWindow ? { marker: NOT_EMPLOYED_MARKER } : {}),
      });
      return;
    }

    dayRecords.forEach((record, index) => {
      rows.push({
        // Split-shift days print the date on the FIRST row only (C9) — the
        // paper analogue of the screen's rowSpan.
        date: index === 0 ? dateYMD : '',
        dayAbbr: index === 0 ? dayAbbr : '',
        isWeekend,
        kind: 'record',
        ...formatRecordCells(record),
      });
    });
  });

  const stats = computeMonthStats(dayGroups);
  // TEN totals — deliberately NO totalDays key (C13: without a filter it
  // restates the row count, and on a partial month it reads as "N days
  // accounted for"). Hours formatted here so the drawing layer only prints.
  const totals = {
    daysWorked: stats.daysWorked,
    pieceRateDays: stats.pieceRateDays,
    leaveDays: stats.leaveDays,
    absentDays: stats.absentDays,
    lateDays: stats.lateDays,
    totalOTHours: stats.totalOTHours.toFixed(2),
    totalNightDiff: stats.totalNightDiff.toFixed(2),
    totalUtHours: stats.totalUtHours.toFixed(2),
    totalRegularHours: stats.totalRegularHours.toFixed(2),
    // Hours, 2dp, like the four beside it — a month of breaks in minutes
    // reads as "1200" (PRD_AttendanceSummaryReport C7). Not drawn by the DTR.
    totalBreakHours: (stats.totalBreakMinutes / 60).toFixed(2),
  };

  // C8's ONE whole-month predicate, decided here in the tested model and
  // driving BOTH derived strings — the PDF layer reads them and derives
  // nothing (it has no test file to catch a second, drifting copy).
  const isWholeMonth =
    rangeStartYMD.slice(0, 7) === rangeEndYMD.slice(0, 7) &&
    rangeStartYMD.endsWith('-01') &&
    rangeEndYMD === toYMDString(endOfMonth(days[days.length - 1]));
  const periodLabel = isWholeMonth
    ? days[0].toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : `${formatYMDForDisplay(rangeStartYMD)} – ${formatYMDForDisplay(rangeEndYMD)}`;
  const fileStamp = isWholeMonth
    ? rangeStartYMD.slice(0, 7)
    : `${rangeStartYMD}_${rangeEndYMD}`;

  const personal = employee?.personalInfo || {};
  const header = {
    companyName: company?.name || '', // name ONLY — no address field at all (C6)
    employeeName:
      personal.fullName || [personal.firstName, personal.lastName].filter(Boolean).join(' '),
    employeeId: employee?.employeeId || '',
    department: employee?.employment?.department || '',
    periodLabel,
    fileStamp,
    periodStart: rangeStartYMD,
    periodEnd: rangeEndYMD,
    // Manila wall-clock of the instant, like todayYMD above — an AU browser's
    // local HH:mm on a Manila document would be 2-3h wrong. en-GB = 24h "14:30".
    generatedAt: `${todayYMD} ${today.toLocaleTimeString('en-GB', {
      timeZone: 'Asia/Manila',
      hour: '2-digit',
      minute: '2-digit',
    })}`,
  };

  // PRD_DtrDateRange C9 — three branches, exhaustive and mutually exclusive
  // (the first two can only overlap when end < start, which C4/C5 block):
  // fully past → complete; wholly future → a distinct notice (the old
  // predicate printed "records through <today>" — a coverage date BEFORE the
  // range's first day); otherwise → PARTIAL through today, unchanged.
  let partialStamp = null;
  if (rangeStartYMD > todayYMD) {
    partialStamp = FUTURE_RANGE_NOTICE;
  } else if (rangeEndYMD >= todayYMD) {
    partialStamp = `PARTIAL — records through ${todayYMD}`;
  }

  return { header, rows, totals, partialStamp };
};
